/**
 * Live-region text that screen readers announce (A11Y-007, TMMAPP-0097 B10 / §C 28). A live region
 * that mounts already holding its text is often not announced: assistive technology reports
 * *changes* to a region it already knows. So the region mounts empty and the text is written a
 * moment later (`LIVE_TEXT_DELAY_MS`), in a later task than the region's own insertion — an effect
 * alone can run in the same commit as the mount. The text node is owned here, outside React's
 * children, so each change is a real DOM change.
 */
import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

/** Long enough to land in a later task than the region's insertion; short enough to read as now. */
export const LIVE_TEXT_DELAY_MS = 60;

/** The text inside a live region the caller owns (a toast's message-only `role="status"`). */
export function LiveText({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (node === null) return undefined;
    const timer = window.setTimeout(() => {
      node.textContent = text;
    }, LIVE_TEXT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [text]);
  return <span ref={ref} />;
}

/** A visually hidden polite status region, mounted empty, then given `text`. */
export function LiveRegion({ text }: { text: string }) {
  return (
    <Box component="span" role="status" aria-live="polite" sx={visuallyHidden}>
      <LiveText text={text} />
    </Box>
  );
}
